import { useState, useEffect, useCallback } from 'react'
import { desktopAppsService, type DesktopAppPosition } from '@/services/desktop-apps-service'
import { logError } from '@/lib/logger'

/**
 * Hook für Desktop-App-Installation Management
 * Nutzt User Settings statt separater Tabelle
 */
export function useDesktopApps() {
  const [installedApps, setInstalledApps] = useState<string[]>([])
  const [loadFailed, setLoadFailed] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Load installed apps
  const loadInstalledApps = useCallback(async () => {
    try {
      setIsLoading(true)
      const apps = await desktopAppsService.getInstalledApps()
      setInstalledApps(apps)
      setLoadFailed(false)
    } catch (error) {
      // Der zuletzt geladene Stand bleibt stehen; der Desktop sagt, dass er
      // unvollständig ist, statt still leer oder mit Standard-Apps zu wirken.
      logError(error as Error, { context: 'useDesktopApps.loadInstalledApps' })
      setLoadFailed(true)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    loadInstalledApps()

    // Listen for settings updates
    const handleSettingsUpdate = () => {
      loadInstalledApps()
    }

    window.addEventListener('settings-updated', handleSettingsUpdate)
    return () => {
      window.removeEventListener('settings-updated', handleSettingsUpdate)
    }
  }, [loadInstalledApps])

  /**
   * Installiert eine App auf dem Desktop
   */
  const installApp = useCallback(
    async (appId: string, position?: DesktopAppPosition) => {
      try {
        await desktopAppsService.installApp(appId, position)
        await loadInstalledApps()
      } catch (error) {
        logError(error as Error, { context: 'useDesktopApps.installApp', appId })
        throw error
      }
    },
    [loadInstalledApps]
  )

  /**
   * Deinstalliert eine App vom Desktop
   */
  const uninstallApp = useCallback(
    async (appId: string) => {
      try {
        await desktopAppsService.uninstallApp(appId)
        await loadInstalledApps()
      } catch (error) {
        logError(error as Error, { context: 'useDesktopApps.uninstallApp', appId })
        throw error
      }
    },
    [loadInstalledApps]
  )

  /**
   * Aktualisiert die Position einer App
   */
  const updateAppPosition = useCallback(
    async (appId: string, position: DesktopAppPosition) => {
      try {
        await desktopAppsService.updateAppPosition(appId, position)
      } catch (error) {
        logError(error as Error, { context: 'useDesktopApps.updateAppPosition', appId })
        await loadInstalledApps()
      }
    },
    [loadInstalledApps]
  )

  /**
   * Prüft ob eine App installiert ist
   */
  const isAppInstalled = useCallback(
    (appId: string): boolean => {
      return installedApps.includes(appId)
    },
    [installedApps]
  )

  /**
   * Holt die Position einer App (aus desktopIconPositions)
   */
  const getAppPosition = useCallback(
    async (appId: string): Promise<DesktopAppPosition | null> => {
      return await desktopAppsService.getAppPosition(appId)
    },
    []
  )

  /**
   * Findet die nächste freie Position auf dem Desktop
   */
  const findNextFreePosition = useCallback(async (): Promise<DesktopAppPosition> => {
    try {
      const settings = await import('@/services/settings-service').then(m => m.SettingsService.loadUserSettings())
      const positions = settings.desktopIconPositions || {}

      // Erstelle ein Set aller belegten Positionen
      const occupiedPositions = new Set<string>()
      Object.values(positions).forEach((pos) => {
        occupiedPositions.add(`${pos.row}:${pos.col}`)
      })

      // Finde die erste freie Position (von links oben nach rechts unten)
      for (let row = 0; row < 10; row++) {
        for (let col = 0; col < 10; col++) {
          const key = `${row}:${col}`
          if (!occupiedPositions.has(key)) {
            return { row, col }
          }
        }
      }

      // Fallback: Position 0,0
      return { row: 0, col: 0 }
    } catch {
      return { row: 0, col: 0 }
    }
  }, [])

  return {
    installedApps,
    isLoading,
    loadFailed,
    reloadInstalledApps: loadInstalledApps,
    installApp,
    uninstallApp,
    updateAppPosition,
    isAppInstalled,
    getAppPosition,
    findNextFreePosition,
    refreshApps: loadInstalledApps,
  }
}
